/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Documents API
 * API for managing documents within an organization. Supports file upload, metadata management, and custom labels. All operations are scoped to the authenticated user\'s organization. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Request body for bulk document capabilities lookup
 */
export interface BulkDocumentCapabilitiesRequest {
    /**
     * List of document UUIDs to get capabilities for (max 50)
     */
    'documentIds': Array<string>;
}
export interface CreateDocumentLabelRequest {
    /**
     * Label name
     */
    'name': string;
    /**
     * Hex color code
     */
    'color': string;
}
/**
 * One bulk application of a default to the cliënten missing it, and how far it has got.  `createdCount` of `totalClientCount` is the progress a practice reads. The denominator is a snapshot taken when the run was requested rather than a running truth — cliënten created by hand during a run receive the default on creation and are not this run\'s work. 
 */
export interface DefaultDocumentApplyRunResponse {
    'id': string;
    'defaultDocumentId': string;
    'status': DefaultDocumentApplyRunStatus;
    /**
     * How many active cliënten were missing the document when the run was requested
     */
    'totalClientCount': number;
    /**
     * How many dossiers this run has written the document into so far
     */
    'createdCount': number;
    'requestedAt': number;
    'requestedById': string;
    /**
     * When a worker first picked the run up, or null while it is still queued
     */
    'startedAt'?: number | null;
    /**
     * When the run ended, either way, or null while it is still going
     */
    'finishedAt'?: number | null;
    /**
     * Why the run stopped early, in Dutch and naming no technical cause, or null when it did not. Re-requesting is the retry, and it re-creates nothing.
     */
    'failureReason'?: string | null;
}


/**
 * Where one bulk application of a default has got to. `PENDING` is what the request leaves behind: the work does not run on the request thread, so \"accepted\" and \"started\" are different things. `COMPLETED` means no active cliënt is missing the document any more; `FAILED` means the run stopped with some still missing it, and the rows it did write stay.
 */

export const DefaultDocumentApplyRunStatus = {
    Pending: 'PENDING',
    InProgress: 'IN_PROGRESS',
    Completed: 'COMPLETED',
    Failed: 'FAILED'
} as const;

export type DefaultDocumentApplyRunStatus = typeof DefaultDocumentApplyRunStatus[keyof typeof DefaultDocumentApplyRunStatus];


/**
 * Authorization capabilities for a document
 */
export interface DocumentCapabilitiesResponse {
    /**
     * Whether the user can view this document
     */
    'view': boolean;
    /**
     * Whether the user can download this document
     */
    'download': boolean;
    /**
     * Whether the user can delete this document
     */
    'delete': boolean;
    /**
     * Whether the user can change this document\'s name
     */
    'rename': boolean;
    /**
     * Whether the user can assign the organization\'s labels to this document (`PUT /api/v1/documents/{documentId}/labels`). Decided by `documents.update`, or `documents.update.own` together with membership of the client\'s behandelteam. Creating, renaming and deleting the organization\'s labels is a different action, governed by `documents.manage_labels`. 
     */
    'assignLabels': boolean;
    /**
     * Whether the user can expose/unexpose this document to the client portal
     */
    'expose': boolean;
}
export interface DocumentLabelResponse {
    /**
     * Unique label identifier
     */
    'id': string;
    /**
     * Organization this label belongs to
     */
    'organizationId': string;
    /**
     * Label name
     */
    'name': string;
    /**
     * Hex color code
     */
    'color': string;
    /**
     * Creation timestamp (epoch milliseconds)
     */
    'createdAt': number;
}
export interface DocumentListResponse {
    /**
     * List of documents for current page
     */
    'content': Array<DocumentResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of documents matching criteria
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
export interface DocumentResponse {
    /**
     * Unique document identifier
     */
    'id': string;
    /**
     * Organization this document belongs to
     */
    'organizationId': string;
    /**
     * Client this document belongs to
     */
    'clientId': string;
    /**
     * Zorgtraject this document is linked to (for verwijsbrief documents)
     */
    'zorgtrajectId'?: string | null;
    /**
     * Human-readable description of the linked zorgtraject (e.g., \"Sectie 2 Mono (gestart 15-01-2026)\"). Computed for display in the Scrivio app. Read the zorgtraject itself with getZorgtrajectById.
     */
    'zorgtrajectDescription'?: string | null;
    /**
     * Human-readable name given to the document, or null when it has none of its own. Anything showing a document name shows `displayName` when set and `filename` otherwise. Carries no file extension. 
     */
    'displayName'?: string | null;
    /**
     * Original filename, as uploaded. Also the name a download is served with.
     */
    'filename': string;
    /**
     * MIME type of the file
     */
    'contentType': string;
    /**
     * File size in bytes
     */
    'size': number;
    /**
     * Labels assigned to this document
     */
    'labels'?: Array<DocumentLabelResponse>;
    'documentType'?: DocumentType;
    /**
     * Upload timestamp (epoch milliseconds)
     */
    'uploadedAt': number;
    /**
     * ID of the user who uploaded the document. A Scrivio user id. Outside the app it can only be matched against the `userId` that getClientBehandelaars returns.
     */
    'uploadedById': string;
    /**
     * Display name of the user who uploaded the document
     */
    'uploadedByName': string;
    /**
     * Whether this document is currently exposed to the client via the portal
     */
    'exposedToClient'?: boolean;
    /**
     * Timestamp when the document was exposed (epoch milliseconds), null if not exposed
     */
    'exposedAt'?: number | null;
    /**
     * ID of the user who exposed the document, null if not exposed or auto-exposed. A Scrivio user id. Outside the app it can only be matched against the `userId` that getClientBehandelaars returns.
     */
    'exposedById'?: string | null;
    /**
     * Display name of the user who exposed the document, null if not exposed or auto-exposed
     */
    'exposedByName'?: string | null;
    /**
     * Whether the document was automatically exposed (e.g., via intake form)
     */
    'exposedAutomatically'?: boolean;
    /**
     * Present only on the response to an upload or exposure that asked to notify the client (`notifyClient: true`). True when the mail was handed to delivery after the change committed; false when it was skipped — the dossier has no e-mailadres, or the request exposed nothing new. The change itself succeeded either way. 
     */
    'clientNotified'?: boolean;
    /**
     * Whether this document requires the client\'s explicit acknowledgment in the portal. Independent of `exposedToClient` — a behandelaar can flag a document as requiring acknowledgment before it is exposed. 
     */
    'requiresAcknowledgment'?: boolean;
    /**
     * Timestamp when the client acknowledged the document (epoch milliseconds), null if not yet acknowledged or not requiring acknowledgment. 
     */
    'acknowledgedAt'?: number | null;
    /**
     * Whether this row came from an organisation default — a document the practice gives every cliënt — rather than being uploaded for this cliënt. The default itself is not identified. 
     */
    'fromOrganizationDefault'?: boolean;
    /**
     * The chain this document belongs to — its own id when it is the first version. Documents sharing a `lineageId` are versions of one document and are shown as versions rather than as loose files. Almost every document stands alone, in which case this is its own id and `version` is 1. 
     */
    'lineageId'?: string;
    /**
     * Position in the lineage, 1-based. A later version supersedes the one before it; neither is deleted, because a regenerated document never overwrites the bytes somebody already received. 
     */
    'version'?: number;
    /**
     * Why this version was produced, as the behandelaar stated it. Null for a first version, which needs no reason — it exists because the document was created. 
     */
    'versionReason'?: string | null;
}


/**
 * Type of document. `GENERATED_LETTER` and `INVOICE` are produced by Scrivio itself — an invoice\'s PDF is rendered once when the invoice is issued and filed in the client\'s dossier — so neither is normally set on an upload.
 */

export const DocumentType = {
    PassportOrId: 'PASSPORT_OR_ID',
    ReferralLetter: 'REFERRAL_LETTER',
    GeneratedLetter: 'GENERATED_LETTER',
    Invoice: 'INVOICE'
} as const;

export type DocumentType = typeof DocumentType[keyof typeof DocumentType];


export interface OrganizationDefaultDocumentResponse {
    'id': string;
    'organizationId': string;
    /**
     * What the practice calls this document
     */
    'displayName': string;
    /**
     * Original filename, as uploaded
     */
    'filename': string;
    'contentType': string;
    /**
     * File size in bytes
     */
    'size': number;
    'documentType'?: DocumentType;
    /**
     * Labels every copy created from this default receives
     */
    'labels'?: Array<DocumentLabelResponse>;
    /**
     * Whether copies created from this default are exposed in the client portal
     */
    'exposedToClient': boolean;
    /**
     * Whether copies created from this default require the cliënt\'s acknowledgment
     */
    'requiresAcknowledgment': boolean;
    'createdAt': number;
    'createdById': string;
    'createdByName': string;
    /**
     * When this stopped being a default, or null while it still is one
     */
    'revokedAt'?: number | null;
    /**
     * How many active cliënten hold no copy of this document. Archived and removed cliënten are not counted. Neither is a cliënt whose copy was later deleted from the dossier: the row is the record that this default was given to them, and a behandelaar who took it out decided something a backfill must not silently undo.  This is the same set the bulk apply acts on, so a non-zero count is exactly the offer \"there is something to backfill\" and zero means there is nothing to do. 
     */
    'missingClientCount': number;
    'latestApplyRun'?: DefaultDocumentApplyRunResponse;
    /**
     * Every version of this document, oldest first and ending with this one. A default that never replaced another has exactly one entry. 
     */
    'versions': Array<OrganizationDefaultDocumentVersion>;
}


/**
 * One version in a default document\'s chain, and how many cliënten accepted it.
 */
export interface OrganizationDefaultDocumentVersion {
    'defaultDocumentId': string;
    /**
     * Position in the chain, 1-based
     */
    'version': number;
    'filename': string;
    'createdAt': number;
    /**
     * How many cliënten hold an acknowledged copy of this version in their dossier
     */
    'acknowledgedClientCount': number;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * Request to update document exposure to client portal
 */
export interface UpdateDocumentExposureRequest {
    /**
     * Whether to expose (true) or unexpose (false) the document to the client portal
     */
    'exposed': boolean;
    /**
     * Mail the client that a new document is waiting in the portal. Opt-in: absent means false. Only honoured when this request actually exposes the document — unexposing, or exposing one that is already exposed, sends nothing. The mail names the practice and never the document. 
     */
    'notifyClient'?: boolean;
}
export interface UpdateDocumentLabelRequest {
    /**
     * Updated label name
     */
    'name'?: string | null;
    /**
     * Hex color code
     */
    'color'?: string | null;
}
export interface UpdateDocumentLabelsRequest {
    /**
     * List of label IDs to assign to the document
     */
    'labelIds': Array<string>;
}
/**
 * Request to set a document\'s human-readable name.  Deliberately not named `UpdateDocumentRequest`: every spec generates its models into one package, so a name that could plausibly be defined by a second spec is one class two contracts fight over (#3222).  `displayName` is deliberately **not** `required`. A required nullable property gets a `@NotNull` on the generated getter, which Hibernate Validator unwraps through `JsonNullable` and applies to the value inside — so an explicit `null`, the one thing this endpoint needs in order to clear a name, would 400. Omitting the property and sending `null` mean the same thing here: clear it. 
 */
export interface UpdateDocumentNameRequest {
    /**
     * The new name, or null to clear it and show the uploaded filename again. A blank string is treated as null. No file extension: downloads keep theirs from the uploaded file. 
     */
    'displayName'?: string | null;
}
/**
 * Request to update whether a document requires the client\'s explicit acknowledgment in the portal. Toggling the flag off does NOT clear an existing acknowledgment. 
 */
export interface UpdateDocumentRequiresAcknowledgmentRequest {
    /**
     * Whether the document requires the client\'s acknowledgment
     */
    'requiresAcknowledgment': boolean;
}
/**
 * The complete settings of a default. This is a replacement, not a patch: every field is written as sent, `labelIds` replaces the label set outright, and an omitted `documentType` clears it. A partial update has no meaning here — the caller is editing a form that shows all of these at once. 
 */
export interface UpdateOrganizationDefaultDocumentRequest {
    'displayName': string;
    'documentType'?: DocumentType;
    /**
     * The labels every copy created from this default receives, replacing the current set
     */
    'labelIds': Array<string>;
    'exposedToClient': boolean;
    'requiresAcknowledgment': boolean;
}



/**
 * DocumentLabelsApi - axios parameter creator
 */
export const DocumentLabelsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Creates a new document label for the organization
         * @summary Create document label
         * @param {CreateDocumentLabelRequest} createDocumentLabelRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createDocumentLabel: async (createDocumentLabelRequest: CreateDocumentLabelRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createDocumentLabelRequest' is not null or undefined
            assertParamExists('createDocumentLabel', 'createDocumentLabelRequest', createDocumentLabelRequest)
            const localVarPath = `/api/v1/document-labels`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createDocumentLabelRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deletes a document label
         * @summary Delete document label
         * @param {string} labelId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteDocumentLabel: async (labelId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'labelId' is not null or undefined
            assertParamExists('deleteDocumentLabel', 'labelId', labelId)
            const localVarPath = `/api/v1/document-labels/{labelId}`
                .replace(`{${"labelId"}}`, encodeURIComponent(String(labelId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all document labels for the organization
         * @summary List document labels
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDocumentLabels: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/document-labels`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates an existing document label
         * @summary Update document label
         * @param {string} labelId 
         * @param {UpdateDocumentLabelRequest} updateDocumentLabelRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDocumentLabel: async (labelId: string, updateDocumentLabelRequest: UpdateDocumentLabelRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'labelId' is not null or undefined
            assertParamExists('updateDocumentLabel', 'labelId', labelId)
            // verify required parameter 'updateDocumentLabelRequest' is not null or undefined
            assertParamExists('updateDocumentLabel', 'updateDocumentLabelRequest', updateDocumentLabelRequest)
            const localVarPath = `/api/v1/document-labels/{labelId}`
                .replace(`{${"labelId"}}`, encodeURIComponent(String(labelId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateDocumentLabelRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * DocumentLabelsApi - functional programming interface
 */
export const DocumentLabelsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = DocumentLabelsApiAxiosParamCreator(configuration)
    return {
        /**
         * Creates a new document label for the organization
         * @summary Create document label
         * @param {CreateDocumentLabelRequest} createDocumentLabelRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createDocumentLabel(createDocumentLabelRequest: CreateDocumentLabelRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentLabelResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createDocumentLabel(createDocumentLabelRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentLabelsApi.createDocumentLabel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deletes a document label
         * @summary Delete document label
         * @param {string} labelId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteDocumentLabel(labelId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteDocumentLabel(labelId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentLabelsApi.deleteDocumentLabel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all document labels for the organization
         * @summary List document labels
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listDocumentLabels(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<DocumentLabelResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listDocumentLabels(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentLabelsApi.listDocumentLabels']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates an existing document label
         * @summary Update document label
         * @param {string} labelId 
         * @param {UpdateDocumentLabelRequest} updateDocumentLabelRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateDocumentLabel(labelId: string, updateDocumentLabelRequest: UpdateDocumentLabelRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentLabelResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateDocumentLabel(labelId, updateDocumentLabelRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentLabelsApi.updateDocumentLabel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * DocumentLabelsApi - factory interface
 */
export const DocumentLabelsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = DocumentLabelsApiFp(configuration)
    return {
        /**
         * Creates a new document label for the organization
         * @summary Create document label
         * @param {CreateDocumentLabelRequest} createDocumentLabelRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createDocumentLabel(createDocumentLabelRequest: CreateDocumentLabelRequest, options?: RawAxiosRequestConfig): AxiosPromise<DocumentLabelResponse> {
            return localVarFp.createDocumentLabel(createDocumentLabelRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Deletes a document label
         * @summary Delete document label
         * @param {string} labelId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteDocumentLabel(labelId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteDocumentLabel(labelId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all document labels for the organization
         * @summary List document labels
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDocumentLabels(options?: RawAxiosRequestConfig): AxiosPromise<Array<DocumentLabelResponse>> {
            return localVarFp.listDocumentLabels(options).then((request) => request(axios, basePath));
        },
        /**
         * Updates an existing document label
         * @summary Update document label
         * @param {string} labelId 
         * @param {UpdateDocumentLabelRequest} updateDocumentLabelRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDocumentLabel(labelId: string, updateDocumentLabelRequest: UpdateDocumentLabelRequest, options?: RawAxiosRequestConfig): AxiosPromise<DocumentLabelResponse> {
            return localVarFp.updateDocumentLabel(labelId, updateDocumentLabelRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * DocumentLabelsApi - object-oriented interface
 */
export class DocumentLabelsApi extends BaseAPI {
    /**
     * Creates a new document label for the organization
     * @summary Create document label
     * @param {CreateDocumentLabelRequest} createDocumentLabelRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createDocumentLabel(createDocumentLabelRequest: CreateDocumentLabelRequest, options?: RawAxiosRequestConfig) {
        return DocumentLabelsApiFp(this.configuration).createDocumentLabel(createDocumentLabelRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deletes a document label
     * @summary Delete document label
     * @param {string} labelId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteDocumentLabel(labelId: string, options?: RawAxiosRequestConfig) {
        return DocumentLabelsApiFp(this.configuration).deleteDocumentLabel(labelId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all document labels for the organization
     * @summary List document labels
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listDocumentLabels(options?: RawAxiosRequestConfig) {
        return DocumentLabelsApiFp(this.configuration).listDocumentLabels(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates an existing document label
     * @summary Update document label
     * @param {string} labelId 
     * @param {UpdateDocumentLabelRequest} updateDocumentLabelRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateDocumentLabel(labelId: string, updateDocumentLabelRequest: UpdateDocumentLabelRequest, options?: RawAxiosRequestConfig) {
        return DocumentLabelsApiFp(this.configuration).updateDocumentLabel(labelId, updateDocumentLabelRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * DocumentsApi - axios parameter creator
 */
export const DocumentsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Deletes a document and its associated file
         * @summary Delete document
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteDocument: async (documentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('deleteDocument', 'documentId', documentId)
            const localVarPath = `/api/v1/documents/{documentId}`
                .replace(`{${"documentId"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes the verwijsbrief document link from a zorgtraject and soft-deletes the document
         * @summary Delete verwijsbrief for zorgtraject
         * @param {string} zorgtrajectId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteZorgtrajectVerwijsbrief: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('deleteZorgtrajectVerwijsbrief', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/verwijsbrief`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Downloads the actual file content of a document
         * @summary Download document file
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadDocument: async (documentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('downloadDocument', 'documentId', documentId)
            const localVarPath = `/api/v1/documents/{documentId}/download`
                .replace(`{${"documentId"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/octet-stream,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns authorization capabilities for the authenticated user on multiple documents. Used by list views to determine which actions are available for each document row without making N+1 API calls.  - Maximum 50 document IDs per request - Documents the user cannot view will return `{ \"view\": false }` only - Documents from other organizations or non-existent documents also return `{ \"view\": false }` 
         * @summary Get capabilities for multiple documents
         * @param {BulkDocumentCapabilitiesRequest} bulkDocumentCapabilitiesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBulkDocumentCapabilities: async (bulkDocumentCapabilitiesRequest: BulkDocumentCapabilitiesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bulkDocumentCapabilitiesRequest' is not null or undefined
            assertParamExists('getBulkDocumentCapabilities', 'bulkDocumentCapabilitiesRequest', bulkDocumentCapabilitiesRequest)
            const localVarPath = `/api/v1/documents/capabilities`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bulkDocumentCapabilitiesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns authorization capabilities for a specific document. Used by the frontend to determine which actions the current user can perform.
         * @summary Get document capabilities
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDocumentCapabilities: async (documentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('getDocumentCapabilities', 'documentId', documentId)
            const localVarPath = `/api/v1/documents/{documentId}/capabilities`
                .replace(`{${"documentId"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the verwijsbrief document metadata for a zorgtraject
         * @summary Get verwijsbrief for zorgtraject
         * @param {string} zorgtrajectId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgtrajectVerwijsbrief: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('getZorgtrajectVerwijsbrief', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/verwijsbrief`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated list of documents for a client
         * @summary List documents for client
         * @param {string} clientId 
         * @param {Array<string>} [labelIds] Filter by label IDs
         * @param {DocumentType} [documentType] Filter by document type
         * @param {Array<DocumentType>} [excludeDocumentTypes] Leave out documents of these types. A document with no type is never excluded. The page and its total are cut after the exclusion.
         * @param {string} [search] Case-insensitive partial match on the document\&#39;s name. Matches both &#x60;displayName&#x60; and &#x60;filename&#x60;, so a renamed document is found under either name.
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;uploadedAt,desc\&quot;). Supported fields: uploadedAt, originalFilename, exposedAt, documentType. Any other field falls back to uploadedAt.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDocuments: async (clientId: string, labelIds?: Array<string>, documentType?: DocumentType, excludeDocumentTypes?: Array<DocumentType>, search?: string, page?: number, size?: number, sort?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listDocuments', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/documents`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (labelIds) {
                localVarQueryParameter['labelIds'] = labelIds;
            }

            if (documentType !== undefined) {
                localVarQueryParameter['documentType'] = documentType;
            }

            if (excludeDocumentTypes) {
                localVarQueryParameter['excludeDocumentTypes'] = excludeDocumentTypes;
            }

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Exposes or unexposes a document to the client portal. When a document is exposed, the client can view and download it through the client portal.  **Note:** Auto-exposed documents (e.g., documents attached via intake form) cannot be unexposed through this endpoint. Attempting to do so will return a 409 Conflict. 
         * @summary Update document exposure
         * @param {string} clientId The client\&#39;s unique identifier
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {UpdateDocumentExposureRequest} updateDocumentExposureRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDocumentExposure: async (clientId: string, documentId: string, updateDocumentExposureRequest: UpdateDocumentExposureRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateDocumentExposure', 'clientId', clientId)
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('updateDocumentExposure', 'documentId', documentId)
            // verify required parameter 'updateDocumentExposureRequest' is not null or undefined
            assertParamExists('updateDocumentExposure', 'updateDocumentExposureRequest', updateDocumentExposureRequest)
            const localVarPath = `/api/v1/clients/{clientId}/documents/{documentId}/exposure`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"documentId"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateDocumentExposureRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Replaces all labels on a document with the provided set of label IDs
         * @summary Update document labels
         * @param {string} documentId 
         * @param {UpdateDocumentLabelsRequest} updateDocumentLabelsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDocumentLabels: async (documentId: string, updateDocumentLabelsRequest: UpdateDocumentLabelsRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('updateDocumentLabels', 'documentId', documentId)
            // verify required parameter 'updateDocumentLabelsRequest' is not null or undefined
            assertParamExists('updateDocumentLabels', 'updateDocumentLabelsRequest', updateDocumentLabelsRequest)
            const localVarPath = `/api/v1/documents/{documentId}/labels`
                .replace(`{${"documentId"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateDocumentLabelsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sets the human-readable name shown for a document, or clears it back to the uploaded filename by sending `null` (or a blank string).  The uploaded file is untouched: `filename`, `contentType` and the extension a download is served with come from the upload and never from this name. 
         * @summary Rename document
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {UpdateDocumentNameRequest} updateDocumentNameRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDocumentName: async (documentId: string, updateDocumentNameRequest: UpdateDocumentNameRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('updateDocumentName', 'documentId', documentId)
            // verify required parameter 'updateDocumentNameRequest' is not null or undefined
            assertParamExists('updateDocumentName', 'updateDocumentNameRequest', updateDocumentNameRequest)
            const localVarPath = `/api/v1/documents/{documentId}/name`
                .replace(`{${"documentId"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateDocumentNameRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sets whether a document requires the client\'s explicit acknowledgment in the portal. Toggling the flag off does NOT clear an existing acknowledgment — the audit trail of any prior acknowledgment is preserved.  Independent of `exposedToClient`: a behandelaar can flag a document as requiring acknowledgment before it is exposed to the client. 
         * @summary Update document acknowledgment requirement
         * @param {string} clientId The client\&#39;s unique identifier
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {UpdateDocumentRequiresAcknowledgmentRequest} updateDocumentRequiresAcknowledgmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDocumentRequiresAcknowledgment: async (clientId: string, documentId: string, updateDocumentRequiresAcknowledgmentRequest: UpdateDocumentRequiresAcknowledgmentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateDocumentRequiresAcknowledgment', 'clientId', clientId)
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('updateDocumentRequiresAcknowledgment', 'documentId', documentId)
            // verify required parameter 'updateDocumentRequiresAcknowledgmentRequest' is not null or undefined
            assertParamExists('updateDocumentRequiresAcknowledgment', 'updateDocumentRequiresAcknowledgmentRequest', updateDocumentRequiresAcknowledgmentRequest)
            const localVarPath = `/api/v1/clients/{clientId}/documents/{documentId}/requires-acknowledgment`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"documentId"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateDocumentRequiresAcknowledgmentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Exposes or unexposes a verwijsbrief document to the client portal. When a document is exposed, the client can view and download it through the client portal.  **Note:** Auto-exposed documents cannot be unexposed through this endpoint. Attempting to do so will return a 409 Conflict. 
         * @summary Update verwijsbrief exposure
         * @param {string} zorgtrajectId The zorgtraject\&#39;s unique identifier
         * @param {UpdateDocumentExposureRequest} updateDocumentExposureRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateZorgtrajectVerwijsbriefExposure: async (zorgtrajectId: string, updateDocumentExposureRequest: UpdateDocumentExposureRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('updateZorgtrajectVerwijsbriefExposure', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'updateDocumentExposureRequest' is not null or undefined
            assertParamExists('updateZorgtrajectVerwijsbriefExposure', 'updateDocumentExposureRequest', updateDocumentExposureRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/verwijsbrief/exposure`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateDocumentExposureRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Uploads a document for a client. Accepts multipart form data with the file and optional metadata.
         * @summary Upload document
         * @param {string} clientId 
         * @param {File} file The file to upload
         * @param {string | null} [displayName] Human-readable name for the document, shown wherever a document name is displayed. Omit it (or send blank) to keep showing the uploaded filename — a camera capture is called &#x60;image.jpg&#x60; on every phone, which is what this exists to replace.  The file extension is not part of this name: downloads keep their extension and &#x60;Content-Type&#x60; from the uploaded file, not from here. 
         * @param {string | null} [labelIds] Comma-separated list of label UUIDs to assign to the document
         * @param {DocumentType} [documentType] 
         * @param {boolean | null} [exposedToClient] Whether to expose this document to the client portal. Defaults to false if not specified.
         * @param {boolean | null} [notifyClient] Mail the client that a new document is waiting in the portal. Opt-in: absent or null means false. Only honoured when the document is actually exposed by this upload. The mail names the practice and never the document. 
         * @param {boolean | null} [requiresAcknowledgment] Whether this document requires the client\\\&#39;s explicit acknowledgment in the portal. Defaults to false if not specified. Independent of &#x60;exposedToClient&#x60; — a document can be flagged as requiring acknowledgment before being exposed. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadDocument: async (clientId: string, file: File, displayName?: string | null, labelIds?: string | null, documentType?: DocumentType, exposedToClient?: boolean | null, notifyClient?: boolean | null, requiresAcknowledgment?: boolean | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('uploadDocument', 'clientId', clientId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadDocument', 'file', file)
            const localVarPath = `/api/v1/clients/{clientId}/documents`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }

            if (displayName !== undefined) { 
                localVarFormParams.append('displayName', displayName as any);
            }

            if (labelIds !== undefined) { 
                localVarFormParams.append('labelIds', labelIds as any);
            }

            if (documentType !== undefined) { 
                localVarFormParams.append('documentType', documentType as any);
            }

            if (exposedToClient !== undefined) { 
                localVarFormParams.append('exposedToClient', String(exposedToClient) as any);
            }

            if (notifyClient !== undefined) { 
                localVarFormParams.append('notifyClient', String(notifyClient) as any);
            }

            if (requiresAcknowledgment !== undefined) { 
                localVarFormParams.append('requiresAcknowledgment', String(requiresAcknowledgment) as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Uploads a verwijsbrief (referral letter) document and links it to the specified zorgtraject.
         * @summary Upload verwijsbrief for zorgtraject
         * @param {string} zorgtrajectId 
         * @param {File} file The verwijsbrief file to upload
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadZorgtrajectVerwijsbrief: async (zorgtrajectId: string, file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('uploadZorgtrajectVerwijsbrief', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadZorgtrajectVerwijsbrief', 'file', file)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/verwijsbrief`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the file content of a document for inline viewing (e.g. the document viewer modal). Audited as a VIEW access (deduplicated within the audit window), whereas the download endpoint is audited as a DOWNLOAD on every call (ADR-024). 
         * @summary View document file content inline
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        viewDocumentContent: async (documentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('viewDocumentContent', 'documentId', documentId)
            const localVarPath = `/api/v1/documents/{documentId}/content`
                .replace(`{${"documentId"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/octet-stream,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * DocumentsApi - functional programming interface
 */
export const DocumentsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = DocumentsApiAxiosParamCreator(configuration)
    return {
        /**
         * Deletes a document and its associated file
         * @summary Delete document
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteDocument(documentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteDocument(documentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.deleteDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes the verwijsbrief document link from a zorgtraject and soft-deletes the document
         * @summary Delete verwijsbrief for zorgtraject
         * @param {string} zorgtrajectId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteZorgtrajectVerwijsbrief(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteZorgtrajectVerwijsbrief(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.deleteZorgtrajectVerwijsbrief']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Downloads the actual file content of a document
         * @summary Download document file
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadDocument(documentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadDocument(documentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.downloadDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns authorization capabilities for the authenticated user on multiple documents. Used by list views to determine which actions are available for each document row without making N+1 API calls.  - Maximum 50 document IDs per request - Documents the user cannot view will return `{ \"view\": false }` only - Documents from other organizations or non-existent documents also return `{ \"view\": false }` 
         * @summary Get capabilities for multiple documents
         * @param {BulkDocumentCapabilitiesRequest} bulkDocumentCapabilitiesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBulkDocumentCapabilities(bulkDocumentCapabilitiesRequest: BulkDocumentCapabilitiesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<{ [key: string]: DocumentCapabilitiesResponse; }>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBulkDocumentCapabilities(bulkDocumentCapabilitiesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.getBulkDocumentCapabilities']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns authorization capabilities for a specific document. Used by the frontend to determine which actions the current user can perform.
         * @summary Get document capabilities
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getDocumentCapabilities(documentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentCapabilitiesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getDocumentCapabilities(documentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.getDocumentCapabilities']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the verwijsbrief document metadata for a zorgtraject
         * @summary Get verwijsbrief for zorgtraject
         * @param {string} zorgtrajectId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getZorgtrajectVerwijsbrief(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getZorgtrajectVerwijsbrief(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.getZorgtrajectVerwijsbrief']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated list of documents for a client
         * @summary List documents for client
         * @param {string} clientId 
         * @param {Array<string>} [labelIds] Filter by label IDs
         * @param {DocumentType} [documentType] Filter by document type
         * @param {Array<DocumentType>} [excludeDocumentTypes] Leave out documents of these types. A document with no type is never excluded. The page and its total are cut after the exclusion.
         * @param {string} [search] Case-insensitive partial match on the document\&#39;s name. Matches both &#x60;displayName&#x60; and &#x60;filename&#x60;, so a renamed document is found under either name.
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;uploadedAt,desc\&quot;). Supported fields: uploadedAt, originalFilename, exposedAt, documentType. Any other field falls back to uploadedAt.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listDocuments(clientId: string, labelIds?: Array<string>, documentType?: DocumentType, excludeDocumentTypes?: Array<DocumentType>, search?: string, page?: number, size?: number, sort?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listDocuments(clientId, labelIds, documentType, excludeDocumentTypes, search, page, size, sort, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.listDocuments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Exposes or unexposes a document to the client portal. When a document is exposed, the client can view and download it through the client portal.  **Note:** Auto-exposed documents (e.g., documents attached via intake form) cannot be unexposed through this endpoint. Attempting to do so will return a 409 Conflict. 
         * @summary Update document exposure
         * @param {string} clientId The client\&#39;s unique identifier
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {UpdateDocumentExposureRequest} updateDocumentExposureRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateDocumentExposure(clientId: string, documentId: string, updateDocumentExposureRequest: UpdateDocumentExposureRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateDocumentExposure(clientId, documentId, updateDocumentExposureRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.updateDocumentExposure']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Replaces all labels on a document with the provided set of label IDs
         * @summary Update document labels
         * @param {string} documentId 
         * @param {UpdateDocumentLabelsRequest} updateDocumentLabelsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateDocumentLabels(documentId: string, updateDocumentLabelsRequest: UpdateDocumentLabelsRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateDocumentLabels(documentId, updateDocumentLabelsRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.updateDocumentLabels']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sets the human-readable name shown for a document, or clears it back to the uploaded filename by sending `null` (or a blank string).  The uploaded file is untouched: `filename`, `contentType` and the extension a download is served with come from the upload and never from this name. 
         * @summary Rename document
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {UpdateDocumentNameRequest} updateDocumentNameRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateDocumentName(documentId: string, updateDocumentNameRequest: UpdateDocumentNameRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateDocumentName(documentId, updateDocumentNameRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.updateDocumentName']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sets whether a document requires the client\'s explicit acknowledgment in the portal. Toggling the flag off does NOT clear an existing acknowledgment — the audit trail of any prior acknowledgment is preserved.  Independent of `exposedToClient`: a behandelaar can flag a document as requiring acknowledgment before it is exposed to the client. 
         * @summary Update document acknowledgment requirement
         * @param {string} clientId The client\&#39;s unique identifier
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {UpdateDocumentRequiresAcknowledgmentRequest} updateDocumentRequiresAcknowledgmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateDocumentRequiresAcknowledgment(clientId: string, documentId: string, updateDocumentRequiresAcknowledgmentRequest: UpdateDocumentRequiresAcknowledgmentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateDocumentRequiresAcknowledgment(clientId, documentId, updateDocumentRequiresAcknowledgmentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.updateDocumentRequiresAcknowledgment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Exposes or unexposes a verwijsbrief document to the client portal. When a document is exposed, the client can view and download it through the client portal.  **Note:** Auto-exposed documents cannot be unexposed through this endpoint. Attempting to do so will return a 409 Conflict. 
         * @summary Update verwijsbrief exposure
         * @param {string} zorgtrajectId The zorgtraject\&#39;s unique identifier
         * @param {UpdateDocumentExposureRequest} updateDocumentExposureRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateZorgtrajectVerwijsbriefExposure(zorgtrajectId: string, updateDocumentExposureRequest: UpdateDocumentExposureRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateZorgtrajectVerwijsbriefExposure(zorgtrajectId, updateDocumentExposureRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.updateZorgtrajectVerwijsbriefExposure']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Uploads a document for a client. Accepts multipart form data with the file and optional metadata.
         * @summary Upload document
         * @param {string} clientId 
         * @param {File} file The file to upload
         * @param {string | null} [displayName] Human-readable name for the document, shown wherever a document name is displayed. Omit it (or send blank) to keep showing the uploaded filename — a camera capture is called &#x60;image.jpg&#x60; on every phone, which is what this exists to replace.  The file extension is not part of this name: downloads keep their extension and &#x60;Content-Type&#x60; from the uploaded file, not from here. 
         * @param {string | null} [labelIds] Comma-separated list of label UUIDs to assign to the document
         * @param {DocumentType} [documentType] 
         * @param {boolean | null} [exposedToClient] Whether to expose this document to the client portal. Defaults to false if not specified.
         * @param {boolean | null} [notifyClient] Mail the client that a new document is waiting in the portal. Opt-in: absent or null means false. Only honoured when the document is actually exposed by this upload. The mail names the practice and never the document. 
         * @param {boolean | null} [requiresAcknowledgment] Whether this document requires the client\\\&#39;s explicit acknowledgment in the portal. Defaults to false if not specified. Independent of &#x60;exposedToClient&#x60; — a document can be flagged as requiring acknowledgment before being exposed. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadDocument(clientId: string, file: File, displayName?: string | null, labelIds?: string | null, documentType?: DocumentType, exposedToClient?: boolean | null, notifyClient?: boolean | null, requiresAcknowledgment?: boolean | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadDocument(clientId, file, displayName, labelIds, documentType, exposedToClient, notifyClient, requiresAcknowledgment, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.uploadDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Uploads a verwijsbrief (referral letter) document and links it to the specified zorgtraject.
         * @summary Upload verwijsbrief for zorgtraject
         * @param {string} zorgtrajectId 
         * @param {File} file The verwijsbrief file to upload
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadZorgtrajectVerwijsbrief(zorgtrajectId: string, file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadZorgtrajectVerwijsbrief(zorgtrajectId, file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.uploadZorgtrajectVerwijsbrief']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the file content of a document for inline viewing (e.g. the document viewer modal). Audited as a VIEW access (deduplicated within the audit window), whereas the download endpoint is audited as a DOWNLOAD on every call (ADR-024). 
         * @summary View document file content inline
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async viewDocumentContent(documentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.viewDocumentContent(documentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DocumentsApi.viewDocumentContent']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * DocumentsApi - factory interface
 */
export const DocumentsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = DocumentsApiFp(configuration)
    return {
        /**
         * Deletes a document and its associated file
         * @summary Delete document
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteDocument(documentId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteDocument(documentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes the verwijsbrief document link from a zorgtraject and soft-deletes the document
         * @summary Delete verwijsbrief for zorgtraject
         * @param {string} zorgtrajectId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteZorgtrajectVerwijsbrief(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteZorgtrajectVerwijsbrief(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Downloads the actual file content of a document
         * @summary Download document file
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadDocument(documentId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadDocument(documentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns authorization capabilities for the authenticated user on multiple documents. Used by list views to determine which actions are available for each document row without making N+1 API calls.  - Maximum 50 document IDs per request - Documents the user cannot view will return `{ \"view\": false }` only - Documents from other organizations or non-existent documents also return `{ \"view\": false }` 
         * @summary Get capabilities for multiple documents
         * @param {BulkDocumentCapabilitiesRequest} bulkDocumentCapabilitiesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBulkDocumentCapabilities(bulkDocumentCapabilitiesRequest: BulkDocumentCapabilitiesRequest, options?: RawAxiosRequestConfig): AxiosPromise<{ [key: string]: DocumentCapabilitiesResponse; }> {
            return localVarFp.getBulkDocumentCapabilities(bulkDocumentCapabilitiesRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns authorization capabilities for a specific document. Used by the frontend to determine which actions the current user can perform.
         * @summary Get document capabilities
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDocumentCapabilities(documentId: string, options?: RawAxiosRequestConfig): AxiosPromise<DocumentCapabilitiesResponse> {
            return localVarFp.getDocumentCapabilities(documentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the verwijsbrief document metadata for a zorgtraject
         * @summary Get verwijsbrief for zorgtraject
         * @param {string} zorgtrajectId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgtrajectVerwijsbrief(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<DocumentResponse> {
            return localVarFp.getZorgtrajectVerwijsbrief(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated list of documents for a client
         * @summary List documents for client
         * @param {string} clientId 
         * @param {Array<string>} [labelIds] Filter by label IDs
         * @param {DocumentType} [documentType] Filter by document type
         * @param {Array<DocumentType>} [excludeDocumentTypes] Leave out documents of these types. A document with no type is never excluded. The page and its total are cut after the exclusion.
         * @param {string} [search] Case-insensitive partial match on the document\&#39;s name. Matches both &#x60;displayName&#x60; and &#x60;filename&#x60;, so a renamed document is found under either name.
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;uploadedAt,desc\&quot;). Supported fields: uploadedAt, originalFilename, exposedAt, documentType. Any other field falls back to uploadedAt.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDocuments(clientId: string, labelIds?: Array<string>, documentType?: DocumentType, excludeDocumentTypes?: Array<DocumentType>, search?: string, page?: number, size?: number, sort?: string, options?: RawAxiosRequestConfig): AxiosPromise<DocumentListResponse> {
            return localVarFp.listDocuments(clientId, labelIds, documentType, excludeDocumentTypes, search, page, size, sort, options).then((request) => request(axios, basePath));
        },
        /**
         * Exposes or unexposes a document to the client portal. When a document is exposed, the client can view and download it through the client portal.  **Note:** Auto-exposed documents (e.g., documents attached via intake form) cannot be unexposed through this endpoint. Attempting to do so will return a 409 Conflict. 
         * @summary Update document exposure
         * @param {string} clientId The client\&#39;s unique identifier
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {UpdateDocumentExposureRequest} updateDocumentExposureRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDocumentExposure(clientId: string, documentId: string, updateDocumentExposureRequest: UpdateDocumentExposureRequest, options?: RawAxiosRequestConfig): AxiosPromise<DocumentResponse> {
            return localVarFp.updateDocumentExposure(clientId, documentId, updateDocumentExposureRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Replaces all labels on a document with the provided set of label IDs
         * @summary Update document labels
         * @param {string} documentId 
         * @param {UpdateDocumentLabelsRequest} updateDocumentLabelsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDocumentLabels(documentId: string, updateDocumentLabelsRequest: UpdateDocumentLabelsRequest, options?: RawAxiosRequestConfig): AxiosPromise<DocumentResponse> {
            return localVarFp.updateDocumentLabels(documentId, updateDocumentLabelsRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Sets the human-readable name shown for a document, or clears it back to the uploaded filename by sending `null` (or a blank string).  The uploaded file is untouched: `filename`, `contentType` and the extension a download is served with come from the upload and never from this name. 
         * @summary Rename document
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {UpdateDocumentNameRequest} updateDocumentNameRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDocumentName(documentId: string, updateDocumentNameRequest: UpdateDocumentNameRequest, options?: RawAxiosRequestConfig): AxiosPromise<DocumentResponse> {
            return localVarFp.updateDocumentName(documentId, updateDocumentNameRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Sets whether a document requires the client\'s explicit acknowledgment in the portal. Toggling the flag off does NOT clear an existing acknowledgment — the audit trail of any prior acknowledgment is preserved.  Independent of `exposedToClient`: a behandelaar can flag a document as requiring acknowledgment before it is exposed to the client. 
         * @summary Update document acknowledgment requirement
         * @param {string} clientId The client\&#39;s unique identifier
         * @param {string} documentId The document\&#39;s unique identifier
         * @param {UpdateDocumentRequiresAcknowledgmentRequest} updateDocumentRequiresAcknowledgmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDocumentRequiresAcknowledgment(clientId: string, documentId: string, updateDocumentRequiresAcknowledgmentRequest: UpdateDocumentRequiresAcknowledgmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<DocumentResponse> {
            return localVarFp.updateDocumentRequiresAcknowledgment(clientId, documentId, updateDocumentRequiresAcknowledgmentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Exposes or unexposes a verwijsbrief document to the client portal. When a document is exposed, the client can view and download it through the client portal.  **Note:** Auto-exposed documents cannot be unexposed through this endpoint. Attempting to do so will return a 409 Conflict. 
         * @summary Update verwijsbrief exposure
         * @param {string} zorgtrajectId The zorgtraject\&#39;s unique identifier
         * @param {UpdateDocumentExposureRequest} updateDocumentExposureRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateZorgtrajectVerwijsbriefExposure(zorgtrajectId: string, updateDocumentExposureRequest: UpdateDocumentExposureRequest, options?: RawAxiosRequestConfig): AxiosPromise<DocumentResponse> {
            return localVarFp.updateZorgtrajectVerwijsbriefExposure(zorgtrajectId, updateDocumentExposureRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Uploads a document for a client. Accepts multipart form data with the file and optional metadata.
         * @summary Upload document
         * @param {string} clientId 
         * @param {File} file The file to upload
         * @param {string | null} [displayName] Human-readable name for the document, shown wherever a document name is displayed. Omit it (or send blank) to keep showing the uploaded filename — a camera capture is called &#x60;image.jpg&#x60; on every phone, which is what this exists to replace.  The file extension is not part of this name: downloads keep their extension and &#x60;Content-Type&#x60; from the uploaded file, not from here. 
         * @param {string | null} [labelIds] Comma-separated list of label UUIDs to assign to the document
         * @param {DocumentType} [documentType] 
         * @param {boolean | null} [exposedToClient] Whether to expose this document to the client portal. Defaults to false if not specified.
         * @param {boolean | null} [notifyClient] Mail the client that a new document is waiting in the portal. Opt-in: absent or null means false. Only honoured when the document is actually exposed by this upload. The mail names the practice and never the document. 
         * @param {boolean | null} [requiresAcknowledgment] Whether this document requires the client\\\&#39;s explicit acknowledgment in the portal. Defaults to false if not specified. Independent of &#x60;exposedToClient&#x60; — a document can be flagged as requiring acknowledgment before being exposed. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadDocument(clientId: string, file: File, displayName?: string | null, labelIds?: string | null, documentType?: DocumentType, exposedToClient?: boolean | null, notifyClient?: boolean | null, requiresAcknowledgment?: boolean | null, options?: RawAxiosRequestConfig): AxiosPromise<DocumentResponse> {
            return localVarFp.uploadDocument(clientId, file, displayName, labelIds, documentType, exposedToClient, notifyClient, requiresAcknowledgment, options).then((request) => request(axios, basePath));
        },
        /**
         * Uploads a verwijsbrief (referral letter) document and links it to the specified zorgtraject.
         * @summary Upload verwijsbrief for zorgtraject
         * @param {string} zorgtrajectId 
         * @param {File} file The verwijsbrief file to upload
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadZorgtrajectVerwijsbrief(zorgtrajectId: string, file: File, options?: RawAxiosRequestConfig): AxiosPromise<DocumentResponse> {
            return localVarFp.uploadZorgtrajectVerwijsbrief(zorgtrajectId, file, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the file content of a document for inline viewing (e.g. the document viewer modal). Audited as a VIEW access (deduplicated within the audit window), whereas the download endpoint is audited as a DOWNLOAD on every call (ADR-024). 
         * @summary View document file content inline
         * @param {string} documentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        viewDocumentContent(documentId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.viewDocumentContent(documentId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * DocumentsApi - object-oriented interface
 */
export class DocumentsApi extends BaseAPI {
    /**
     * Deletes a document and its associated file
     * @summary Delete document
     * @param {string} documentId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteDocument(documentId: string, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).deleteDocument(documentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes the verwijsbrief document link from a zorgtraject and soft-deletes the document
     * @summary Delete verwijsbrief for zorgtraject
     * @param {string} zorgtrajectId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteZorgtrajectVerwijsbrief(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).deleteZorgtrajectVerwijsbrief(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Downloads the actual file content of a document
     * @summary Download document file
     * @param {string} documentId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadDocument(documentId: string, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).downloadDocument(documentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns authorization capabilities for the authenticated user on multiple documents. Used by list views to determine which actions are available for each document row without making N+1 API calls.  - Maximum 50 document IDs per request - Documents the user cannot view will return `{ \"view\": false }` only - Documents from other organizations or non-existent documents also return `{ \"view\": false }` 
     * @summary Get capabilities for multiple documents
     * @param {BulkDocumentCapabilitiesRequest} bulkDocumentCapabilitiesRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBulkDocumentCapabilities(bulkDocumentCapabilitiesRequest: BulkDocumentCapabilitiesRequest, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).getBulkDocumentCapabilities(bulkDocumentCapabilitiesRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns authorization capabilities for a specific document. Used by the frontend to determine which actions the current user can perform.
     * @summary Get document capabilities
     * @param {string} documentId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getDocumentCapabilities(documentId: string, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).getDocumentCapabilities(documentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the verwijsbrief document metadata for a zorgtraject
     * @summary Get verwijsbrief for zorgtraject
     * @param {string} zorgtrajectId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getZorgtrajectVerwijsbrief(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).getZorgtrajectVerwijsbrief(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated list of documents for a client
     * @summary List documents for client
     * @param {string} clientId 
     * @param {Array<string>} [labelIds] Filter by label IDs
     * @param {DocumentType} [documentType] Filter by document type
     * @param {Array<DocumentType>} [excludeDocumentTypes] Leave out documents of these types. A document with no type is never excluded. The page and its total are cut after the exclusion.
     * @param {string} [search] Case-insensitive partial match on the document\&#39;s name. Matches both &#x60;displayName&#x60; and &#x60;filename&#x60;, so a renamed document is found under either name.
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;uploadedAt,desc\&quot;). Supported fields: uploadedAt, originalFilename, exposedAt, documentType. Any other field falls back to uploadedAt.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listDocuments(clientId: string, labelIds?: Array<string>, documentType?: DocumentType, excludeDocumentTypes?: Array<DocumentType>, search?: string, page?: number, size?: number, sort?: string, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).listDocuments(clientId, labelIds, documentType, excludeDocumentTypes, search, page, size, sort, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Exposes or unexposes a document to the client portal. When a document is exposed, the client can view and download it through the client portal.  **Note:** Auto-exposed documents (e.g., documents attached via intake form) cannot be unexposed through this endpoint. Attempting to do so will return a 409 Conflict. 
     * @summary Update document exposure
     * @param {string} clientId The client\&#39;s unique identifier
     * @param {string} documentId The document\&#39;s unique identifier
     * @param {UpdateDocumentExposureRequest} updateDocumentExposureRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateDocumentExposure(clientId: string, documentId: string, updateDocumentExposureRequest: UpdateDocumentExposureRequest, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).updateDocumentExposure(clientId, documentId, updateDocumentExposureRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Replaces all labels on a document with the provided set of label IDs
     * @summary Update document labels
     * @param {string} documentId 
     * @param {UpdateDocumentLabelsRequest} updateDocumentLabelsRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateDocumentLabels(documentId: string, updateDocumentLabelsRequest: UpdateDocumentLabelsRequest, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).updateDocumentLabels(documentId, updateDocumentLabelsRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sets the human-readable name shown for a document, or clears it back to the uploaded filename by sending `null` (or a blank string).  The uploaded file is untouched: `filename`, `contentType` and the extension a download is served with come from the upload and never from this name. 
     * @summary Rename document
     * @param {string} documentId The document\&#39;s unique identifier
     * @param {UpdateDocumentNameRequest} updateDocumentNameRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateDocumentName(documentId: string, updateDocumentNameRequest: UpdateDocumentNameRequest, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).updateDocumentName(documentId, updateDocumentNameRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sets whether a document requires the client\'s explicit acknowledgment in the portal. Toggling the flag off does NOT clear an existing acknowledgment — the audit trail of any prior acknowledgment is preserved.  Independent of `exposedToClient`: a behandelaar can flag a document as requiring acknowledgment before it is exposed to the client. 
     * @summary Update document acknowledgment requirement
     * @param {string} clientId The client\&#39;s unique identifier
     * @param {string} documentId The document\&#39;s unique identifier
     * @param {UpdateDocumentRequiresAcknowledgmentRequest} updateDocumentRequiresAcknowledgmentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateDocumentRequiresAcknowledgment(clientId: string, documentId: string, updateDocumentRequiresAcknowledgmentRequest: UpdateDocumentRequiresAcknowledgmentRequest, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).updateDocumentRequiresAcknowledgment(clientId, documentId, updateDocumentRequiresAcknowledgmentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Exposes or unexposes a verwijsbrief document to the client portal. When a document is exposed, the client can view and download it through the client portal.  **Note:** Auto-exposed documents cannot be unexposed through this endpoint. Attempting to do so will return a 409 Conflict. 
     * @summary Update verwijsbrief exposure
     * @param {string} zorgtrajectId The zorgtraject\&#39;s unique identifier
     * @param {UpdateDocumentExposureRequest} updateDocumentExposureRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateZorgtrajectVerwijsbriefExposure(zorgtrajectId: string, updateDocumentExposureRequest: UpdateDocumentExposureRequest, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).updateZorgtrajectVerwijsbriefExposure(zorgtrajectId, updateDocumentExposureRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Uploads a document for a client. Accepts multipart form data with the file and optional metadata.
     * @summary Upload document
     * @param {string} clientId 
     * @param {File} file The file to upload
     * @param {string | null} [displayName] Human-readable name for the document, shown wherever a document name is displayed. Omit it (or send blank) to keep showing the uploaded filename — a camera capture is called &#x60;image.jpg&#x60; on every phone, which is what this exists to replace.  The file extension is not part of this name: downloads keep their extension and &#x60;Content-Type&#x60; from the uploaded file, not from here. 
     * @param {string | null} [labelIds] Comma-separated list of label UUIDs to assign to the document
     * @param {DocumentType} [documentType] 
     * @param {boolean | null} [exposedToClient] Whether to expose this document to the client portal. Defaults to false if not specified.
     * @param {boolean | null} [notifyClient] Mail the client that a new document is waiting in the portal. Opt-in: absent or null means false. Only honoured when the document is actually exposed by this upload. The mail names the practice and never the document. 
     * @param {boolean | null} [requiresAcknowledgment] Whether this document requires the client\\\&#39;s explicit acknowledgment in the portal. Defaults to false if not specified. Independent of &#x60;exposedToClient&#x60; — a document can be flagged as requiring acknowledgment before being exposed. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadDocument(clientId: string, file: File, displayName?: string | null, labelIds?: string | null, documentType?: DocumentType, exposedToClient?: boolean | null, notifyClient?: boolean | null, requiresAcknowledgment?: boolean | null, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).uploadDocument(clientId, file, displayName, labelIds, documentType, exposedToClient, notifyClient, requiresAcknowledgment, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Uploads a verwijsbrief (referral letter) document and links it to the specified zorgtraject.
     * @summary Upload verwijsbrief for zorgtraject
     * @param {string} zorgtrajectId 
     * @param {File} file The verwijsbrief file to upload
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadZorgtrajectVerwijsbrief(zorgtrajectId: string, file: File, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).uploadZorgtrajectVerwijsbrief(zorgtrajectId, file, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the file content of a document for inline viewing (e.g. the document viewer modal). Audited as a VIEW access (deduplicated within the audit window), whereas the download endpoint is audited as a DOWNLOAD on every call (ADR-024). 
     * @summary View document file content inline
     * @param {string} documentId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public viewDocumentContent(documentId: string, options?: RawAxiosRequestConfig) {
        return DocumentsApiFp(this.configuration).viewDocumentContent(documentId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * OrganizationDefaultDocumentsApi - axios parameter creator
 */
export const OrganizationDefaultDocumentsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Uploads a file and registers it as an organisation-wide default document. The settings given here are what every copy created in a cliënt dossier inherits.  Creating a default writes no `documents` row on its own — the cliënten who should receive it are applied separately, so publishing a default is never an unannounced bulk write. 
         * @summary Add an organisation default document
         * @param {File} file The file to upload
         * @param {string} displayName What the practice calls this document. Required, unlike a dossier document\\\&#39;s display name — a default is picked from a list by name before anyone has opened the file. 
         * @param {string | null} [labelIds] Comma-separated list of label UUIDs every created copy receives
         * @param {DocumentType} [documentType] 
         * @param {boolean | null} [exposedToClient] Whether created copies are exposed in the client portal. False if not specified.
         * @param {boolean | null} [requiresAcknowledgment] Whether created copies require the cliënt\\\&#39;s explicit acknowledgment in the portal. False if not specified. Independent of &#x60;exposedToClient&#x60;. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createOrganizationDefaultDocument: async (file: File, displayName: string, labelIds?: string | null, documentType?: DocumentType, exposedToClient?: boolean | null, requiresAcknowledgment?: boolean | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'file' is not null or undefined
            assertParamExists('createOrganizationDefaultDocument', 'file', file)
            // verify required parameter 'displayName' is not null or undefined
            assertParamExists('createOrganizationDefaultDocument', 'displayName', displayName)
            const localVarPath = `/api/v1/organizations/me/default-documents`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }

            if (displayName !== undefined) { 
                localVarFormParams.append('displayName', displayName as any);
            }

            if (labelIds !== undefined) { 
                localVarFormParams.append('labelIds', labelIds as any);
            }

            if (documentType !== undefined) { 
                localVarFormParams.append('documentType', documentType as any);
            }

            if (exposedToClient !== undefined) { 
                localVarFormParams.append('exposedToClient', String(exposedToClient) as any);
            }

            if (requiresAcknowledgment !== undefined) { 
                localVarFormParams.append('requiresAcknowledgment', String(requiresAcknowledgment) as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Uploads a new file as the next version of this default. The new default carries the name, type, labels and portal settings of the one it replaces; the one it replaces is revoked, and the new version is applied to the cliënten in the background exactly as `startOrganizationDefaultDocumentApply` would (the response\'s `latestApplyRun`).  Each cliënt\'s copy becomes the next version of their copy of the replaced default, in the same lineage, so the dossier shows one document with its history. The replaced copy leaves the portal: removed where nobody acknowledged it, kept with its acknowledgment where somebody did.  `reacknowledgmentRequired` is the practice\'s answer to \"must cliënten accept again\". With `false`, a cliënt who acknowledged the replaced version gets the new one without being asked again — their acknowledgment of the earlier version stays the evidence. Every other cliënt is asked as `requiresAcknowledgment` says. 
         * @summary Replace a default document with a new version
         * @param {string} defaultDocumentId 
         * @param {File} file The new version\\\&#39;s file
         * @param {boolean} reacknowledgmentRequired Whether a cliënt who acknowledged the replaced version must acknowledge this one again. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createOrganizationDefaultDocumentVersion: async (defaultDocumentId: string, file: File, reacknowledgmentRequired: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'defaultDocumentId' is not null or undefined
            assertParamExists('createOrganizationDefaultDocumentVersion', 'defaultDocumentId', defaultDocumentId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('createOrganizationDefaultDocumentVersion', 'file', file)
            // verify required parameter 'reacknowledgmentRequired' is not null or undefined
            assertParamExists('createOrganizationDefaultDocumentVersion', 'reacknowledgmentRequired', reacknowledgmentRequired)
            const localVarPath = `/api/v1/organizations/me/default-documents/{defaultDocumentId}/versions`
                .replace(`{${"defaultDocumentId"}}`, encodeURIComponent(String(defaultDocumentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }

            if (reacknowledgmentRequired !== undefined) { 
                localVarFormParams.append('reacknowledgmentRequired', String(reacknowledgmentRequired) as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get one organisation default document
         * @param {string} defaultDocumentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationDefaultDocument: async (defaultDocumentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'defaultDocumentId' is not null or undefined
            assertParamExists('getOrganizationDefaultDocument', 'defaultDocumentId', defaultDocumentId)
            const localVarPath = `/api/v1/organizations/me/default-documents/{defaultDocumentId}`
                .replace(`{${"defaultDocumentId"}}`, encodeURIComponent(String(defaultDocumentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the organisation\'s default documents, each with the number of active cliënten that do not yet hold a copy of it. 
         * @summary List the organisation\'s default documents
         * @param {boolean} [includeRevoked] Include defaults that have been revoked. Revoked defaults are kept because the dossier documents they produced still point at them. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizationDefaultDocuments: async (includeRevoked?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/me/default-documents`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (includeRevoked !== undefined) {
                localVarQueryParameter['includeRevoked'] = includeRevoked;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stops this document being a default. The row is kept rather than deleted: the dossier documents created from it still point here, and that FK is what explains where they came from. 
         * @summary Revoke an organisation default document
         * @param {string} defaultDocumentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeOrganizationDefaultDocument: async (defaultDocumentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'defaultDocumentId' is not null or undefined
            assertParamExists('revokeOrganizationDefaultDocument', 'defaultDocumentId', defaultDocumentId)
            const localVarPath = `/api/v1/organizations/me/default-documents/{defaultDocumentId}`
                .replace(`{${"defaultDocumentId"}}`, encodeURIComponent(String(defaultDocumentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Starts applying this default to every active cliënt of the organisation that does not yet hold a copy of it. Publishing a default to the whole practice and backfilling after an EPD import are the same operation over different cliënten, and `missingClientCount` is exactly how many this will act on.  **The work does not run on this request.** Creating a copy for 300 or 5.000 cliënten is thousands of writes, each fanning out further work per cliënt, so the response is a `202` with a run that a scheduled worker then advances. Poll the default itself to watch it: `latestApplyRun` carries the counts and `missingClientCount` falls as it goes.  Running it twice creates nothing the second time — the cliënten are selected by *not* holding the document, so a completed run leaves an empty candidate set. A cliënt whose copy was deleted from the dossier is not re-given one either; that removal was a decision. 
         * @summary Apply a default document to the cliënten missing it
         * @param {string} defaultDocumentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        startOrganizationDefaultDocumentApply: async (defaultDocumentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'defaultDocumentId' is not null or undefined
            assertParamExists('startOrganizationDefaultDocumentApply', 'defaultDocumentId', defaultDocumentId)
            const localVarPath = `/api/v1/organizations/me/default-documents/{defaultDocumentId}/apply-runs`
                .replace(`{${"defaultDocumentId"}}`, encodeURIComponent(String(defaultDocumentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Changes the name, type, labels and inherited portal settings of a default. The stored file is not replaceable — a new file is a new default, so that the copies already in dossiers keep naming the bytes a cliënt actually acknowledged.  Changing a setting here does not change the dossier documents already created from this default; it decides what the next copy inherits. 
         * @summary Update an organisation default document\'s settings
         * @param {string} defaultDocumentId 
         * @param {UpdateOrganizationDefaultDocumentRequest} updateOrganizationDefaultDocumentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationDefaultDocument: async (defaultDocumentId: string, updateOrganizationDefaultDocumentRequest: UpdateOrganizationDefaultDocumentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'defaultDocumentId' is not null or undefined
            assertParamExists('updateOrganizationDefaultDocument', 'defaultDocumentId', defaultDocumentId)
            // verify required parameter 'updateOrganizationDefaultDocumentRequest' is not null or undefined
            assertParamExists('updateOrganizationDefaultDocument', 'updateOrganizationDefaultDocumentRequest', updateOrganizationDefaultDocumentRequest)
            const localVarPath = `/api/v1/organizations/me/default-documents/{defaultDocumentId}`
                .replace(`{${"defaultDocumentId"}}`, encodeURIComponent(String(defaultDocumentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateOrganizationDefaultDocumentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * OrganizationDefaultDocumentsApi - functional programming interface
 */
export const OrganizationDefaultDocumentsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = OrganizationDefaultDocumentsApiAxiosParamCreator(configuration)
    return {
        /**
         * Uploads a file and registers it as an organisation-wide default document. The settings given here are what every copy created in a cliënt dossier inherits.  Creating a default writes no `documents` row on its own — the cliënten who should receive it are applied separately, so publishing a default is never an unannounced bulk write. 
         * @summary Add an organisation default document
         * @param {File} file The file to upload
         * @param {string} displayName What the practice calls this document. Required, unlike a dossier document\\\&#39;s display name — a default is picked from a list by name before anyone has opened the file. 
         * @param {string | null} [labelIds] Comma-separated list of label UUIDs every created copy receives
         * @param {DocumentType} [documentType] 
         * @param {boolean | null} [exposedToClient] Whether created copies are exposed in the client portal. False if not specified.
         * @param {boolean | null} [requiresAcknowledgment] Whether created copies require the cliënt\\\&#39;s explicit acknowledgment in the portal. False if not specified. Independent of &#x60;exposedToClient&#x60;. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createOrganizationDefaultDocument(file: File, displayName: string, labelIds?: string | null, documentType?: DocumentType, exposedToClient?: boolean | null, requiresAcknowledgment?: boolean | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrganizationDefaultDocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createOrganizationDefaultDocument(file, displayName, labelIds, documentType, exposedToClient, requiresAcknowledgment, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationDefaultDocumentsApi.createOrganizationDefaultDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Uploads a new file as the next version of this default. The new default carries the name, type, labels and portal settings of the one it replaces; the one it replaces is revoked, and the new version is applied to the cliënten in the background exactly as `startOrganizationDefaultDocumentApply` would (the response\'s `latestApplyRun`).  Each cliënt\'s copy becomes the next version of their copy of the replaced default, in the same lineage, so the dossier shows one document with its history. The replaced copy leaves the portal: removed where nobody acknowledged it, kept with its acknowledgment where somebody did.  `reacknowledgmentRequired` is the practice\'s answer to \"must cliënten accept again\". With `false`, a cliënt who acknowledged the replaced version gets the new one without being asked again — their acknowledgment of the earlier version stays the evidence. Every other cliënt is asked as `requiresAcknowledgment` says. 
         * @summary Replace a default document with a new version
         * @param {string} defaultDocumentId 
         * @param {File} file The new version\\\&#39;s file
         * @param {boolean} reacknowledgmentRequired Whether a cliënt who acknowledged the replaced version must acknowledge this one again. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createOrganizationDefaultDocumentVersion(defaultDocumentId: string, file: File, reacknowledgmentRequired: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrganizationDefaultDocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createOrganizationDefaultDocumentVersion(defaultDocumentId, file, reacknowledgmentRequired, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationDefaultDocumentsApi.createOrganizationDefaultDocumentVersion']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get one organisation default document
         * @param {string} defaultDocumentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOrganizationDefaultDocument(defaultDocumentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrganizationDefaultDocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOrganizationDefaultDocument(defaultDocumentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationDefaultDocumentsApi.getOrganizationDefaultDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the organisation\'s default documents, each with the number of active cliënten that do not yet hold a copy of it. 
         * @summary List the organisation\'s default documents
         * @param {boolean} [includeRevoked] Include defaults that have been revoked. Revoked defaults are kept because the dossier documents they produced still point at them. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listOrganizationDefaultDocuments(includeRevoked?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<OrganizationDefaultDocumentResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listOrganizationDefaultDocuments(includeRevoked, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationDefaultDocumentsApi.listOrganizationDefaultDocuments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stops this document being a default. The row is kept rather than deleted: the dossier documents created from it still point here, and that FK is what explains where they came from. 
         * @summary Revoke an organisation default document
         * @param {string} defaultDocumentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async revokeOrganizationDefaultDocument(defaultDocumentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.revokeOrganizationDefaultDocument(defaultDocumentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationDefaultDocumentsApi.revokeOrganizationDefaultDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Starts applying this default to every active cliënt of the organisation that does not yet hold a copy of it. Publishing a default to the whole practice and backfilling after an EPD import are the same operation over different cliënten, and `missingClientCount` is exactly how many this will act on.  **The work does not run on this request.** Creating a copy for 300 or 5.000 cliënten is thousands of writes, each fanning out further work per cliënt, so the response is a `202` with a run that a scheduled worker then advances. Poll the default itself to watch it: `latestApplyRun` carries the counts and `missingClientCount` falls as it goes.  Running it twice creates nothing the second time — the cliënten are selected by *not* holding the document, so a completed run leaves an empty candidate set. A cliënt whose copy was deleted from the dossier is not re-given one either; that removal was a decision. 
         * @summary Apply a default document to the cliënten missing it
         * @param {string} defaultDocumentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async startOrganizationDefaultDocumentApply(defaultDocumentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DefaultDocumentApplyRunResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.startOrganizationDefaultDocumentApply(defaultDocumentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationDefaultDocumentsApi.startOrganizationDefaultDocumentApply']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Changes the name, type, labels and inherited portal settings of a default. The stored file is not replaceable — a new file is a new default, so that the copies already in dossiers keep naming the bytes a cliënt actually acknowledged.  Changing a setting here does not change the dossier documents already created from this default; it decides what the next copy inherits. 
         * @summary Update an organisation default document\'s settings
         * @param {string} defaultDocumentId 
         * @param {UpdateOrganizationDefaultDocumentRequest} updateOrganizationDefaultDocumentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateOrganizationDefaultDocument(defaultDocumentId: string, updateOrganizationDefaultDocumentRequest: UpdateOrganizationDefaultDocumentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrganizationDefaultDocumentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateOrganizationDefaultDocument(defaultDocumentId, updateOrganizationDefaultDocumentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationDefaultDocumentsApi.updateOrganizationDefaultDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * OrganizationDefaultDocumentsApi - factory interface
 */
export const OrganizationDefaultDocumentsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = OrganizationDefaultDocumentsApiFp(configuration)
    return {
        /**
         * Uploads a file and registers it as an organisation-wide default document. The settings given here are what every copy created in a cliënt dossier inherits.  Creating a default writes no `documents` row on its own — the cliënten who should receive it are applied separately, so publishing a default is never an unannounced bulk write. 
         * @summary Add an organisation default document
         * @param {File} file The file to upload
         * @param {string} displayName What the practice calls this document. Required, unlike a dossier document\\\&#39;s display name — a default is picked from a list by name before anyone has opened the file. 
         * @param {string | null} [labelIds] Comma-separated list of label UUIDs every created copy receives
         * @param {DocumentType} [documentType] 
         * @param {boolean | null} [exposedToClient] Whether created copies are exposed in the client portal. False if not specified.
         * @param {boolean | null} [requiresAcknowledgment] Whether created copies require the cliënt\\\&#39;s explicit acknowledgment in the portal. False if not specified. Independent of &#x60;exposedToClient&#x60;. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createOrganizationDefaultDocument(file: File, displayName: string, labelIds?: string | null, documentType?: DocumentType, exposedToClient?: boolean | null, requiresAcknowledgment?: boolean | null, options?: RawAxiosRequestConfig): AxiosPromise<OrganizationDefaultDocumentResponse> {
            return localVarFp.createOrganizationDefaultDocument(file, displayName, labelIds, documentType, exposedToClient, requiresAcknowledgment, options).then((request) => request(axios, basePath));
        },
        /**
         * Uploads a new file as the next version of this default. The new default carries the name, type, labels and portal settings of the one it replaces; the one it replaces is revoked, and the new version is applied to the cliënten in the background exactly as `startOrganizationDefaultDocumentApply` would (the response\'s `latestApplyRun`).  Each cliënt\'s copy becomes the next version of their copy of the replaced default, in the same lineage, so the dossier shows one document with its history. The replaced copy leaves the portal: removed where nobody acknowledged it, kept with its acknowledgment where somebody did.  `reacknowledgmentRequired` is the practice\'s answer to \"must cliënten accept again\". With `false`, a cliënt who acknowledged the replaced version gets the new one without being asked again — their acknowledgment of the earlier version stays the evidence. Every other cliënt is asked as `requiresAcknowledgment` says. 
         * @summary Replace a default document with a new version
         * @param {string} defaultDocumentId 
         * @param {File} file The new version\\\&#39;s file
         * @param {boolean} reacknowledgmentRequired Whether a cliënt who acknowledged the replaced version must acknowledge this one again. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createOrganizationDefaultDocumentVersion(defaultDocumentId: string, file: File, reacknowledgmentRequired: boolean, options?: RawAxiosRequestConfig): AxiosPromise<OrganizationDefaultDocumentResponse> {
            return localVarFp.createOrganizationDefaultDocumentVersion(defaultDocumentId, file, reacknowledgmentRequired, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get one organisation default document
         * @param {string} defaultDocumentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationDefaultDocument(defaultDocumentId: string, options?: RawAxiosRequestConfig): AxiosPromise<OrganizationDefaultDocumentResponse> {
            return localVarFp.getOrganizationDefaultDocument(defaultDocumentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the organisation\'s default documents, each with the number of active cliënten that do not yet hold a copy of it. 
         * @summary List the organisation\'s default documents
         * @param {boolean} [includeRevoked] Include defaults that have been revoked. Revoked defaults are kept because the dossier documents they produced still point at them. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizationDefaultDocuments(includeRevoked?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<Array<OrganizationDefaultDocumentResponse>> {
            return localVarFp.listOrganizationDefaultDocuments(includeRevoked, options).then((request) => request(axios, basePath));
        },
        /**
         * Stops this document being a default. The row is kept rather than deleted: the dossier documents created from it still point here, and that FK is what explains where they came from. 
         * @summary Revoke an organisation default document
         * @param {string} defaultDocumentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeOrganizationDefaultDocument(defaultDocumentId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.revokeOrganizationDefaultDocument(defaultDocumentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Starts applying this default to every active cliënt of the organisation that does not yet hold a copy of it. Publishing a default to the whole practice and backfilling after an EPD import are the same operation over different cliënten, and `missingClientCount` is exactly how many this will act on.  **The work does not run on this request.** Creating a copy for 300 or 5.000 cliënten is thousands of writes, each fanning out further work per cliënt, so the response is a `202` with a run that a scheduled worker then advances. Poll the default itself to watch it: `latestApplyRun` carries the counts and `missingClientCount` falls as it goes.  Running it twice creates nothing the second time — the cliënten are selected by *not* holding the document, so a completed run leaves an empty candidate set. A cliënt whose copy was deleted from the dossier is not re-given one either; that removal was a decision. 
         * @summary Apply a default document to the cliënten missing it
         * @param {string} defaultDocumentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        startOrganizationDefaultDocumentApply(defaultDocumentId: string, options?: RawAxiosRequestConfig): AxiosPromise<DefaultDocumentApplyRunResponse> {
            return localVarFp.startOrganizationDefaultDocumentApply(defaultDocumentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Changes the name, type, labels and inherited portal settings of a default. The stored file is not replaceable — a new file is a new default, so that the copies already in dossiers keep naming the bytes a cliënt actually acknowledged.  Changing a setting here does not change the dossier documents already created from this default; it decides what the next copy inherits. 
         * @summary Update an organisation default document\'s settings
         * @param {string} defaultDocumentId 
         * @param {UpdateOrganizationDefaultDocumentRequest} updateOrganizationDefaultDocumentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationDefaultDocument(defaultDocumentId: string, updateOrganizationDefaultDocumentRequest: UpdateOrganizationDefaultDocumentRequest, options?: RawAxiosRequestConfig): AxiosPromise<OrganizationDefaultDocumentResponse> {
            return localVarFp.updateOrganizationDefaultDocument(defaultDocumentId, updateOrganizationDefaultDocumentRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * OrganizationDefaultDocumentsApi - object-oriented interface
 */
export class OrganizationDefaultDocumentsApi extends BaseAPI {
    /**
     * Uploads a file and registers it as an organisation-wide default document. The settings given here are what every copy created in a cliënt dossier inherits.  Creating a default writes no `documents` row on its own — the cliënten who should receive it are applied separately, so publishing a default is never an unannounced bulk write. 
     * @summary Add an organisation default document
     * @param {File} file The file to upload
     * @param {string} displayName What the practice calls this document. Required, unlike a dossier document\\\&#39;s display name — a default is picked from a list by name before anyone has opened the file. 
     * @param {string | null} [labelIds] Comma-separated list of label UUIDs every created copy receives
     * @param {DocumentType} [documentType] 
     * @param {boolean | null} [exposedToClient] Whether created copies are exposed in the client portal. False if not specified.
     * @param {boolean | null} [requiresAcknowledgment] Whether created copies require the cliënt\\\&#39;s explicit acknowledgment in the portal. False if not specified. Independent of &#x60;exposedToClient&#x60;. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createOrganizationDefaultDocument(file: File, displayName: string, labelIds?: string | null, documentType?: DocumentType, exposedToClient?: boolean | null, requiresAcknowledgment?: boolean | null, options?: RawAxiosRequestConfig) {
        return OrganizationDefaultDocumentsApiFp(this.configuration).createOrganizationDefaultDocument(file, displayName, labelIds, documentType, exposedToClient, requiresAcknowledgment, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Uploads a new file as the next version of this default. The new default carries the name, type, labels and portal settings of the one it replaces; the one it replaces is revoked, and the new version is applied to the cliënten in the background exactly as `startOrganizationDefaultDocumentApply` would (the response\'s `latestApplyRun`).  Each cliënt\'s copy becomes the next version of their copy of the replaced default, in the same lineage, so the dossier shows one document with its history. The replaced copy leaves the portal: removed where nobody acknowledged it, kept with its acknowledgment where somebody did.  `reacknowledgmentRequired` is the practice\'s answer to \"must cliënten accept again\". With `false`, a cliënt who acknowledged the replaced version gets the new one without being asked again — their acknowledgment of the earlier version stays the evidence. Every other cliënt is asked as `requiresAcknowledgment` says. 
     * @summary Replace a default document with a new version
     * @param {string} defaultDocumentId 
     * @param {File} file The new version\\\&#39;s file
     * @param {boolean} reacknowledgmentRequired Whether a cliënt who acknowledged the replaced version must acknowledge this one again. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createOrganizationDefaultDocumentVersion(defaultDocumentId: string, file: File, reacknowledgmentRequired: boolean, options?: RawAxiosRequestConfig) {
        return OrganizationDefaultDocumentsApiFp(this.configuration).createOrganizationDefaultDocumentVersion(defaultDocumentId, file, reacknowledgmentRequired, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get one organisation default document
     * @param {string} defaultDocumentId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOrganizationDefaultDocument(defaultDocumentId: string, options?: RawAxiosRequestConfig) {
        return OrganizationDefaultDocumentsApiFp(this.configuration).getOrganizationDefaultDocument(defaultDocumentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the organisation\'s default documents, each with the number of active cliënten that do not yet hold a copy of it. 
     * @summary List the organisation\'s default documents
     * @param {boolean} [includeRevoked] Include defaults that have been revoked. Revoked defaults are kept because the dossier documents they produced still point at them. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listOrganizationDefaultDocuments(includeRevoked?: boolean, options?: RawAxiosRequestConfig) {
        return OrganizationDefaultDocumentsApiFp(this.configuration).listOrganizationDefaultDocuments(includeRevoked, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stops this document being a default. The row is kept rather than deleted: the dossier documents created from it still point here, and that FK is what explains where they came from. 
     * @summary Revoke an organisation default document
     * @param {string} defaultDocumentId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public revokeOrganizationDefaultDocument(defaultDocumentId: string, options?: RawAxiosRequestConfig) {
        return OrganizationDefaultDocumentsApiFp(this.configuration).revokeOrganizationDefaultDocument(defaultDocumentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Starts applying this default to every active cliënt of the organisation that does not yet hold a copy of it. Publishing a default to the whole practice and backfilling after an EPD import are the same operation over different cliënten, and `missingClientCount` is exactly how many this will act on.  **The work does not run on this request.** Creating a copy for 300 or 5.000 cliënten is thousands of writes, each fanning out further work per cliënt, so the response is a `202` with a run that a scheduled worker then advances. Poll the default itself to watch it: `latestApplyRun` carries the counts and `missingClientCount` falls as it goes.  Running it twice creates nothing the second time — the cliënten are selected by *not* holding the document, so a completed run leaves an empty candidate set. A cliënt whose copy was deleted from the dossier is not re-given one either; that removal was a decision. 
     * @summary Apply a default document to the cliënten missing it
     * @param {string} defaultDocumentId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public startOrganizationDefaultDocumentApply(defaultDocumentId: string, options?: RawAxiosRequestConfig) {
        return OrganizationDefaultDocumentsApiFp(this.configuration).startOrganizationDefaultDocumentApply(defaultDocumentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Changes the name, type, labels and inherited portal settings of a default. The stored file is not replaceable — a new file is a new default, so that the copies already in dossiers keep naming the bytes a cliënt actually acknowledged.  Changing a setting here does not change the dossier documents already created from this default; it decides what the next copy inherits. 
     * @summary Update an organisation default document\'s settings
     * @param {string} defaultDocumentId 
     * @param {UpdateOrganizationDefaultDocumentRequest} updateOrganizationDefaultDocumentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateOrganizationDefaultDocument(defaultDocumentId: string, updateOrganizationDefaultDocumentRequest: UpdateOrganizationDefaultDocumentRequest, options?: RawAxiosRequestConfig) {
        return OrganizationDefaultDocumentsApiFp(this.configuration).updateOrganizationDefaultDocument(defaultDocumentId, updateOrganizationDefaultDocumentRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



